import { createAppSlice } from '@store/createAppSlice';
import { freezeAccount, freezeProducts, getFreezeDetails, unfreezeAccount, unfreezeProducts } from 'common/api';
import { changeDate, formatDate } from 'common/dateUtils';
import type { AgreementId, CommercialId } from 'common/services/api.types';
import type { SoftwareProduct } from 'modules/softwareProducts/useSoftwareProducts';
import { useMessageStore } from '../hooks/useMessageStore';

const initialState = {
  details: {
    loading: false,
    loaded: false,
    failed: false,
    saving: false,
    hasFreezeScheduled: undefined as boolean | undefined,
    scheduledStartDate: '',
    scheduledEndDate: '',
  },
  selectedToFreeze: [] as SoftwareProduct[],
  selectedToUnfreeze: [] as SoftwareProduct[],
  startDate: formatDate(changeDate(new Date(), { months: 1 }), 'shortDateFirstOfMonth'),
  endDate: formatDate(changeDate(new Date(), { months: 2 }), 'shortDateFirstOfMonth'),
};

const freezeSlice = createAppSlice({
  name: 'freeze',
  initialState,
  reducers: (create) => ({
    freezeProducts: create.asyncThunk(
      (
        list: {
          commercialProductId: CommercialId;
          agreementDetailsId: AgreementId;
          startDate: string;
          endDate: string;
        }[],
      ) => freezeProducts(list),
      {
        rejected: () => {
          useMessageStore.getState().addMessage({
            title: 'En feil oppstod ved frysing av produkter',
            type: 'danger',
          });
        },
      },
    ),
    unfreezeProducts: create.asyncThunk((list: AgreementId[]) => unfreezeProducts(list)),
    freezeAccount: create.asyncThunk(({ from, to }: { from: string; to: string }) => freezeAccount(from, to), {
      rejected: () => {
        useMessageStore.getState().addMessage({
          title: 'En feil oppstod ved frysing av kontoen',
          type: 'danger',
        });
      },
    }),
    unfreezeAccount: create.asyncThunk(() => unfreezeAccount()),
    getFreezeDetails: create.asyncThunk(() => getFreezeDetails(), {
      pending: (state) => {
        state.details.loading = true;
      },
      fulfilled: (state, action) => {
        state.details.loading = false;
        state.details.loaded = true;
        Object.assign(state.details, action.payload);
      },
      rejected: (state) => {
        state.details.loading = false;
        state.details.failed = true;
        useMessageStore.getState().addMessage({
          title: 'En feil oppstod under henting av frysedetaljer',
          type: 'danger',
        });
      },
    }),
    reloadFreezeDetails: create.reducer((state) => {
      state.details.loaded = false;
    }),
    // freeze
    addProductToFreeze: create.reducer<SoftwareProduct>((state, action) => {
      state.selectedToFreeze.push(action.payload);
    }),
    removeProductToFreeze: create.reducer<Pick<SoftwareProduct, 'agreementDetailId'>>((state, action) => {
      state.selectedToFreeze = state.selectedToFreeze.filter(
        (x) => x.agreementDetailId !== action.payload.agreementDetailId,
      );
    }),
    // unfreeze
    addProductToUnfreeze: create.reducer<SoftwareProduct | SoftwareProduct[]>((state, action) => {
      if (Array.isArray(action.payload)) {
        state.selectedToUnfreeze.push(...action.payload);
      } else {
        state.selectedToUnfreeze.push(action.payload);
      }
    }),
    removeProductFromUnfreeze: create.reducer<SoftwareProduct | SoftwareProduct[]>((state, action) => {
      const data = Array.isArray(action.payload) ? action.payload : [action.payload];
      state.selectedToUnfreeze = state.selectedToUnfreeze.filter(
        (x) => !data.some((p) => p.agreementDetailId === x.agreementDetailId),
      );
    }),
    resetSelectedProducts: create.reducer<void>((state) => {
      state.selectedToFreeze = [];
      state.selectedToUnfreeze = [];
    }),
    setEndDate: create.reducer<string>((state, action) => {
      state.endDate = action.payload;
    }),
    setStartDate: create.reducer<string>((state, action) => {
      state.startDate = action.payload;
    }),
  }),
  selectors: {
    getFreeze: (state) => state,
    getCustomerFreezeDetails: (state) => state.details,
    shouldLoadCustomerFreezeDetails: (state) => state.details.loaded !== true,
    isFreezeLoaded: (state) => state.details.loaded === true,
    getSelectedToFreeze: (state) => state.selectedToFreeze || _emptyList,
    getSelectedToUnfreeze: (state) => state.selectedToUnfreeze || _emptyList,
    getStartDate: (state) => state.startDate,
    getEndDate: (state) => state.endDate,
  },
});
const _emptyList: SoftwareProduct[] = [];

export const actions = freezeSlice.actions;
export const selectors = freezeSlice.selectors;
export default freezeSlice.reducer;
